import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Trash2, ChevronDown, ChevronRight, Mail, Phone, Search,
  FileText, ExternalLink, Briefcase, Calendar,
} from "lucide-react";

const STATUSES = [
  { key: "new",         label: "New",         color: "bg-[color:var(--honey)] text-[color:var(--blue-ink)]" },
  { key: "reviewing",   label: "Reviewing",   color: "bg-blue-100 text-blue-800" },
  { key: "interviewed", label: "Interviewed", color: "bg-purple-100 text-purple-800" },
  { key: "hired",       label: "Hired",       color: "bg-emerald-100 text-emerald-800" },
  { key: "closed",      label: "Closed",      color: "bg-slate-200 text-slate-700" },
];

function StatusBadge({ status }) {
  const s = STATUSES.find((x) => x.key === status) || STATUSES[0];
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.color}`}>
      {s.label}
    </span>
  );
}

const RESUME_URL = (a) => {
  if (!a.resume_url) return null;
  return a.resume_url.startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL}${a.resume_url}`
    : a.resume_url;
};

export default function AdminHiring() {
  const { authedClient } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await authedClient().get("/admin/hiring/applications");
      setApps(data);
    } catch {
      toast.error("Could not load applications");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line

  const setStatus = async (id, status) => {
    try {
      await authedClient().patch(`/admin/hiring/applications/${id}`, { status });
      toast.success(`Marked ${status}`);
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application permanently? This cannot be undone.")) return;
    try {
      await authedClient().delete(`/admin/hiring/applications/${id}`);
      toast.success("Deleted");
      setApps((prev) => prev.filter((a) => a.id !== id));
    } catch {
      toast.error("Delete failed");
    }
  };

  const counts = useMemo(() => {
    const c = { all: apps.length };
    STATUSES.forEach((s) => { c[s.key] = 0; });
    for (const a of apps) c[a.status || "new"] = (c[a.status || "new"] || 0) + 1;
    return c;
  }, [apps]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return apps.filter((a) => {
      if (filter !== "all" && (a.status || "new") !== filter) return false;
      if (!needle) return true;
      const hay = [a.name, a.email, a.phone, a.position].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [apps, q, filter]);

  return (
    <div className="max-w-6xl mx-auto" data-testid="admin-hiring">
      <header className="mb-6">
        <h1 className="font-display text-3xl text-[color:var(--blue-ink)] flex items-center gap-2">
          <Briefcase size={26} className="text-[color:var(--honey)]" /> Job Applications
        </h1>
        <p className="text-sm text-[color:var(--muted)] mt-1">
          Applications submitted through the online Hiring page. Review resumes and update status here.
        </p>
      </header>

      <div className="flex flex-wrap gap-2 items-center mb-5">
        <button
          onClick={() => setFilter("all")}
          className={`text-xs px-3 py-1 rounded-full border ${filter === "all" ? "bg-[color:var(--blue-ink)] text-white border-[color:var(--blue-ink)]" : "bg-white border-[color:var(--line)]"}`}
          data-testid="filter-all"
        >
          All ({counts.all})
        </button>
        {STATUSES.map((s) => (
          <button
            key={s.key}
            onClick={() => setFilter(s.key)}
            className={`text-xs px-3 py-1 rounded-full border ${filter === s.key ? "bg-[color:var(--blue-ink)] text-white border-[color:var(--blue-ink)]" : "bg-white border-[color:var(--line)]"}`}
            data-testid={`filter-${s.key}`}
          >
            {s.label} ({counts[s.key] || 0})
          </button>
        ))}
        <div className="ml-auto relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--muted)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, phone..."
            className="pl-8 pr-3 py-1.5 text-sm rounded-full border border-[color:var(--line)] bg-white w-64"
            data-testid="admin-hiring-search"
          />
        </div>
      </div>

      {loading ? (
        <p className="text-center py-16 text-[color:var(--muted)]">Loading applications...</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[color:var(--line)] p-12 text-center">
          <p className="text-[color:var(--muted)]">
            {apps.length === 0
              ? "No applications yet. When someone applies through the Hiring page, they'll show up here."
              : "No applications match your filter."}
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((a) => (
            <li key={a.id} className="rounded-2xl border border-[color:var(--line)] bg-white overflow-hidden">
              <div className="flex flex-wrap items-center gap-3 p-4">
                <button
                  onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                  className="text-[color:var(--muted)] hover:text-[color:var(--blue-ink)]"
                  data-testid={`toggle-${a.id}`}
                >
                  {expanded === a.id ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                </button>
                <div className="flex-1 min-w-[220px]">
                  <div className="font-medium text-[color:var(--blue-ink)]">{a.name}</div>
                  <div className="text-xs text-[color:var(--ink-soft)] flex flex-wrap gap-x-3 gap-y-1 mt-0.5">
                    <span className="inline-flex items-center gap-1"><Mail size={11} /> {a.email}</span>
                    <span className="inline-flex items-center gap-1"><Phone size={11} /> {a.phone}</span>
                    <span className="inline-flex items-center gap-1"><Calendar size={11} /> {new Date(a.submitted_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
                  </div>
                </div>
                <div className="text-xs text-[color:var(--ink-soft)] hidden md:block">{a.position || "Temple Office Administrator"}</div>
                <StatusBadge status={a.status} />
                {RESUME_URL(a) && (
                  <a
                    href={RESUME_URL(a)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-[color:var(--blue-ink)] text-white hover:opacity-90"
                    data-testid={`resume-${a.id}`}
                  >
                    <FileText size={12} /> Resume <ExternalLink size={11} />
                  </a>
                )}
                <select
                  value={a.status || "new"}
                  onChange={(e) => setStatus(a.id, e.target.value)}
                  className="text-xs rounded-full border border-[color:var(--line)] bg-white px-2 py-1.5"
                  data-testid={`status-${a.id}`}
                >
                  {STATUSES.map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
                <button
                  onClick={() => remove(a.id)}
                  className="text-red-600 hover:text-red-800 p-1"
                  title="Delete permanently"
                  data-testid={`delete-${a.id}`}
                  aria-label="Delete application"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              {expanded === a.id && (
                <div className="border-t border-[color:var(--line)] px-4 py-4 bg-[color:var(--cream)]/40 space-y-3 text-sm">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[color:var(--muted)] mb-1">Contact</div>
                    <div className="space-y-1">
                      <div><a href={`mailto:${a.email}`} className="text-[color:var(--blue-deep)] underline">{a.email}</a></div>
                      <div><a href={`tel:${a.phone.replace(/\s+/g,"")}`} className="text-[color:var(--blue-deep)] underline">{a.phone}</a></div>
                    </div>
                  </div>
                  {a.resume_filename && (
                    <div>
                      <div className="text-xs uppercase tracking-wider text-[color:var(--muted)] mb-1">Resume</div>
                      <div className="flex items-center gap-2 text-[color:var(--ink)]">
                        <FileText size={13} />
                        <span>{a.resume_filename}</span>
                        {typeof a.resume_size === "number" && (
                          <span className="text-[color:var(--muted)] text-xs">
                            ({Math.round(a.resume_size / 1024)} KB)
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[color:var(--muted)] mb-1">Submitted</div>
                    <div>{new Date(a.submitted_at).toLocaleString()}</div>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
